import { CustomCompetencyInitializer, CompetencyHelpers } from "../../../../../entities/characters/Competencies";
import { OwnedCompetencyReference } from "../../../../../entities/Ownership";
import { ErrorToast, SuccessToast } from "../../../../../entities/toasts/Toasts";
import { ApiDefaultState } from "../../../api/ApiStore.State";
import { AppThunkAction } from "../../../ApplicationState";
import { ToastAction, ToastDispatchables } from "../../../toasts/Toasts.Actions";
import { CharacterSetEntityPending } from "./CharacterSpecificActions";

export type CharacterAddCompetency = { type: 'CHARACTER_ADD_COMPETENCY', characterId: string, reference: OwnedCompetencyReference }
export type CharacterAddCustomCompetency = { type: 'CHARACTER_ADD_CUSTOM_COMPETENCY', characterId: string, competency: CustomCompetencyInitializer }
export type CharacterRemoveCompetency = { type: 'CHARACTER_REMOVE_COMPETENCY', characterId: string, competencyId: string }
export type CharacterSetCompetencyLevel = { type: 'CHARACTER_SET_COMPETENCY_LEVEL', characterId: string, competencyId: string, isExpert: boolean }
export type CharacterSaveCompetency = { type: 'CHARACTER_SAVE_COMPETENCY', characterId: string, competencyId: string }
export type CharacterSaveCustomCompetency = { type: 'CHARACTER_SAVE_CUSTOM_COMPETENCY', characterId: string, oldCompetencyId: string, newCompetencyId: string }

export type CharacterCompetencyAction = CharacterAddCompetency
    | CharacterAddCustomCompetency
    | CharacterRemoveCompetency
    | CharacterSetCompetencyLevel
    | CharacterSaveCompetency
    | CharacterSaveCustomCompetency;

export const CharacterCompetencyActions = {
    addCompetency: (characterId: string, competencyId: string): AppThunkAction<CharacterCompetencyAction | ToastAction> =>
        async (dispatch, getState) => {
            const competencies = getState().library.competencies;
            const competency = competencies.find(c => c.id == competencyId);
            if (competency == null) {
                throw "Attempted to add a non-existent competency to a character.";
            }

            const undo = () => {
                dispatch({ type: 'CHARACTER_REMOVE_COMPETENCY', characterId, competencyId });
                ToastDispatchables.toastLegacy(new ErrorToast(`Error adding ${competency.name}.`), dispatch);
            }

            const reference: OwnedCompetencyReference = {
                id: competencyId,
                isExpert: false,
            }
            dispatch({ type: 'CHARACTER_ADD_COMPETENCY', characterId, reference });

            try {
                const response = await ApiDefaultState.characters.addCompetency(characterId, competencyId);
                if (response.status == 'Error') {
                    undo();
                    ToastDispatchables.toastValidationResults(response.validationResults, dispatch);
                    return;
                }
                dispatch({ type: 'CHARACTER_SAVE_COMPETENCY', characterId, competencyId });
                ToastDispatchables.toastLegacy(new SuccessToast(`${competency.name} added.`), dispatch);
            }
            catch (e) {
                undo();
                console.error(e);
            }
        },

    addCustomCompetency: (characterId: string, competency: CustomCompetencyInitializer): AppThunkAction<CharacterCompetencyAction | ToastAction> =>
        async (dispatch, getState) => {
            const undo = () => {
                dispatch({ type: 'CHARACTER_REMOVE_COMPETENCY', characterId, competencyId: competency.id });
                ToastDispatchables.toastLegacy(new ErrorToast(`Error adding ${competency.name}.`), dispatch);
            }

            dispatch({ type: 'CHARACTER_ADD_CUSTOM_COMPETENCY', characterId, competency });
            try {
                const response = await ApiDefaultState.characters.addCustomCompetency(characterId, competency);
                if (response.status == 'Error') {
                    undo();
                    ToastDispatchables.toastValidationResults(response.validationResults, dispatch);
                    return;
                }
                dispatch({ type: 'CHARACTER_SAVE_CUSTOM_COMPETENCY', characterId: characterId, oldCompetencyId: competency.id, newCompetencyId: response.payload });
                ToastDispatchables.toastLegacy(new SuccessToast(`${competency.name} added.`), dispatch);
            }
            catch (e) {
                undo();
                console.error(e);
            }
        },

    removeCompetency: (characterId: string, competencyId: string): AppThunkAction<CharacterCompetencyAction | ToastAction> =>
        async (dispatch, getState) => {
            const characters = getState().character.characters;
            const character = characters.find(c => c.id == characterId);
            if (character == null) {
                throw "Attempted to remove competency from nonexistent character.";
            }

            const undo = () => {
                const customCompetency = character.customCompetencies.find(c => c.id == competencyId);
                const reference = character.competencies.find(c => c.id == competencyId);

                if (customCompetency != null) {
                    dispatch({ type: 'CHARACTER_ADD_CUSTOM_COMPETENCY', characterId, competency: customCompetency });
                    ToastDispatchables.toastLegacy(new ErrorToast(`Error removing ${customCompetency.name}.`), dispatch);
                    return;
                }
                else if (reference != null) {
                    const competency = getState().library.competencies.find(c => c.id == competencyId);
                    dispatch({ type: 'CHARACTER_ADD_COMPETENCY', characterId, reference });

                    if (competency != null) {
                        ToastDispatchables.toastLegacy(new ErrorToast(`Error removing ${competency.name}`), dispatch);
                    }
                }
            }

            dispatch({ type: 'CHARACTER_REMOVE_COMPETENCY', characterId, competencyId });

            try {
                const response = await ApiDefaultState.characters.removeCompetency(character.id, competencyId);
                if (response.status == 'Error') {
                    undo();
                    ToastDispatchables.toastValidationResults(response.validationResults, dispatch);
                }
            }
            catch (e) {
                undo();
                console.error(e);
            }
        },

    updateCompetencyLevel: (characterId: string, competencyId: string, isExpert: boolean): AppThunkAction<CharacterCompetencyAction | CharacterSetEntityPending | ToastAction> =>
        async (dispatch, getState) => {
            const characters = getState().character.characters;
            const character = characters.find(c => c.id == characterId);

            if (character == null) {
                throw "Attempted to update competency for nonexistent character.";
            }

            const competencies = getState().library.competencies;
            const competency = competencies.find(c => c.id == competencyId);
            if (competency == null) {
                throw "Attempted to update a nonexistent competency.";
            }

            const reference = character.competencies.find(c => c.id  == competencyId);
            if (reference == null) {
                throw "Competency not found on character.";
            }

            const customCompetency = character.customCompetencies.find(cc => cc.id == competencyId);
            const competencyName = CompetencyHelpers.getName(competency, customCompetency);

            const characterCompetencyReference = character.competencies.find(c => c.id == competencyId);
            const oldIsExpert = characterCompetencyReference?.isExpert ?? customCompetency?.isExpert ?? false;

            const undo = () => {
                dispatch({ type: 'CHARACTER_SET_COMPETENCY_LEVEL', characterId, competencyId, isExpert: oldIsExpert });
                ToastDispatchables.toastLegacy(new ErrorToast(`Error updating ${competencyName}.`), dispatch);
            }

            dispatch({ type: 'CHARACTER_SET_COMPETENCY_LEVEL', characterId, competencyId, isExpert });

            try {
                const response = await ApiDefaultState.characters.updateCompetency(character.id, reference, { ...reference, isExpert });
                if (response.status == 'Error') {
                    undo();
                    ToastDispatchables.toastValidationResults(response.validationResults, dispatch);
                }
            }
            catch (e) {
                undo();
                console.error(e);
            }
            finally {
                dispatch({ type: 'CHARACTER_SET_PENDING', characterId, entityId: reference.id, pending: false });
            }
        }
}